import type { UserAnalyticsItem } from '../../../../../contexts/tableDefinitions';
import './analyticsItem.css';

interface AnalyticsActionProps {
    events: UserAnalyticsItem[];
    actionName: string;
    propertyKeys: string[];
}

function displayValue(value: unknown): string {
    if (value == null) return '(not set)';
    if (typeof value === 'string') return value;
    return JSON.stringify(value) ?? String(value);
}

export default function AnalyticsAction({
    events,
    actionName,
    propertyKeys,
}: AnalyticsActionProps) {
    const now = new Date();
    const currentMonth = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
    const previousDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    const previousMonth = `${previousDate.getUTCFullYear()}-${String(previousDate.getUTCMonth() + 1).padStart(2, '0')}`;

    const groups = new Map<
        string,
        { path: string; values: string[]; currentCount: number; previousCount: number }
    >();

    for (const event of events) {
        if (event.action_name !== actionName) continue;

        const timestamp = new Date(event.timestamp);
        if (Number.isNaN(timestamp.getTime())) continue;

        const eventMonth = `${timestamp.getUTCFullYear()}-${String(timestamp.getUTCMonth() + 1).padStart(2, '0')}`;
        if (eventMonth !== currentMonth && eventMonth !== previousMonth) continue;

        let properties: Record<string, unknown> = {};
        try {
            properties = typeof event.properties === 'string'
                ? JSON.parse(event.properties)
                : event.properties ?? {};
        } catch {
            properties = {};
        }

        const values = propertyKeys.map((key) => displayValue(properties[key]));
        const groupKey = JSON.stringify([event.action_name, event.path, values]);
        let group = groups.get(groupKey);

        if (!group) {
            group = { path: event.path, values, currentCount: 0, previousCount: 0 };
            groups.set(groupKey, group);
        }

        if (eventMonth === currentMonth) group.currentCount += 1;
        else group.previousCount += 1;
    }

    const rows = Array.from(groups.entries()).sort(
        ([, a], [, b]) => b.currentCount - a.currentCount
    );
    
    const categoryTotal = events.filter(
        (event) => event.action_name === actionName
    ).length;

    const compactTotal = new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1,
    })
        .format(categoryTotal)
        .replace('K', 'k');
    
    
    return (
        <div className="analyticsItem-mainContainer">
            <div className="analyticsItem-titleContainer">
                <span>{actionName}</span>
                <span className="analyticsItem-total">{compactTotal}</span>
            </div>
            <div className="analyticsItem-itemContainer">
                {rows.map(([groupKey, row]) => (
                    <div key={groupKey} className="analyticsItem-row">
                        <div className="analyticsItem-itemName">
                            {row.values.map((value, index) => (
                                <div key={propertyKeys[index]}>{value}</div>
                            ))}
                            <div className="analyticsItem-pathName">{row.path}</div>
                        </div>
                        <div className="analyticsItem-count">
                        {row.currentCount.toLocaleString()}
                        <small
                            className={`analyticsItem-change ${
                                row.currentCount > row.previousCount
                                    ? 'analyticsItem-change--positive'
                                    : row.currentCount < row.previousCount
                                        ? 'analyticsItem-change--negative'
                                        : ''
                            }`}
                        >
                            {row.previousCount === 0
                                ? ''
                                : `${row.currentCount > row.previousCount ? '+' : ''}${(
                                    ((row.currentCount - row.previousCount) / row.previousCount) * 100
                                ).toFixed(0)}%`}
                        </small>
                    </div>
                    </div>
                ))}
            </div>
        </div>
    );
}